import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, Header, DeleteModal } from '@common/components';
import TableWithPagination from '@common/components/TableWithPagination';
import Toolbar from '@common/components/Toolbar';
import { searchReports, bulkDelete, updateDashboard } from '@api/dashboard';

import {
  getFilterTypes,
  getFilterValues,
  buildReportFilters,
  STATUS_PUBLISHED,
  STATUS_DRAFT,
  createAdminOnlyCreateButton,
} from './utils';
import { createColumns } from './tableColumns';
import SelectedReportsBar from './SelectedReportsBar';
import * as styles from './styles.module.css';

const emptyFilter = {
  id: Date.now() + Math.random(),
  type: '',
  value: '',
};

export default function ReportsManagement({
  onViewReport,
  onEditReport,
  onCreateReport,
  hideHeader = false, // Hide breadcrumbs and header when embedded
  hideToolbar = false, // Hide toolbar when parent provides unified toolbar
  isAdmin = false, // User admin privileges
  userLoading = false, // Whether user info is still loading
  user = null, // User object - null means no user info available
  // External filter state (when controlled from parent)
  externalSearchTerm,
  externalFilters,
  onSearchChange: externalOnSearchChange,
  onFiltersChange: externalOnFiltersChange,
}) {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState({});
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [columnSizing, setColumnSizing] = useState({});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  // Internal filter state (used when not controlled from parent)
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalFilters, setInternalFilters] = useState([emptyFilter]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [deselectedRowIds, setDeselectedRowIds] = useState({});

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('idle');
  const [deleteResults, setDeleteResults] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  // Determine if using external (controlled) or internal filter state
  const isControlled = externalFilters !== undefined;
  const searchTerm = isControlled ? externalSearchTerm : internalSearchTerm;
  const filters = isControlled ? externalFilters : internalFilters;
  const setSearchTerm = isControlled
    ? externalOnSearchChange
    : setInternalSearchTerm;
  const setFilters = isControlled
    ? externalOnFiltersChange
    : setInternalFilters;

  const getRowId = useCallback((row) => String(row.id), []);

  const refreshData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    // Don't fetch data if user info is still loading or not available
    if (userLoading || !user) {
      setData([]);
      setTotalCount(0);
      return;
    }

    const fetchData = async () => {
      try {
        // Build filters using shared utility function
        const reportFilters = buildReportFilters(filters, isAdmin, true);

        const result = await searchReports({
          page: pageIndex,
          pageSize,
          sortField: sorting[0]?.id || 'updatedAt',
          sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
          filters: reportFilters,
          searchTerm,
        });

        if (result?.rows) {
          setData(result.rows);
          setTotalCount(result.total || 0);
        } else {
          // Handle case where result doesn't have rows property
          setData([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
        setData([]);
        setTotalCount(0);
      }
    };
    fetchData();
  }, [
    pageIndex,
    pageSize,
    sorting,
    searchTerm,
    filters,
    refreshTrigger,
    isAdmin,
    userLoading,
    user,
  ]);

  useEffect(() => {
    setPageIndex(1);
  }, [sorting, searchTerm]);

  const handleView = useCallback(
    (report) => {
      onViewReport(report);
    },
    [onViewReport],
  );

  const handleEdit = useCallback(
    (report) => {
      onEditReport(report);
    },
    [onEditReport],
  );

  const handlePublish = useCallback(
    async (report) => {
      try {
        // Only send the status field we're updating
        const updateData = {
          status: STATUS_PUBLISHED,
        };

        const result = await updateDashboard(report.id, updateData);

        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to publish report:', error);
      }
    },
    [refreshData],
  );

  const handleUnpublish = useCallback(
    async (report) => {
      try {
        // Only send the status field we're updating
        const updateData = {
          status: STATUS_DRAFT,
        };

        const result = await updateDashboard(report.id, updateData);

        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to unpublish report:', error);
      }
    },
    [refreshData],
  );

  // Handler for inline status update via dropdown
  const handleUpdateStatus = useCallback(
    async (report, newStatus) => {
      try {
        const result = await updateDashboard(report.id, { status: newStatus });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to update report status:', error);
      }
    },
    [refreshData],
  );

  const handleDeselectAll = useCallback(() => {
    setSelectedRowIds({});
    setSelectAllMode(false);
    setDeselectedRowIds({});
  }, []);

  const handleDeleteClick = useCallback((report) => {
    setSelectedReport(report);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedReport(null);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
    setOpenAccordion(null);
  }, []);

  const handleDeleteSubmit = useCallback(async () => {
    setDeleteStatus('loading');
    setDeleteError(null);

    try {
      if (selectedReport) {
        // Single delete
        await bulkDelete({ ids: [selectedReport.id] });
        setDeleteResults({
          success: [{ name: selectedReport.name }],
          failed: [],
        });
      } else {
        // Bulk delete
        const selectedIds = Object.keys(selectedRowIds)
          .filter((id) => selectedRowIds[id])
          .map((id) => Number(id));

        const result = await bulkDelete({ ids: selectedIds });

        if (result.status === 'success') {
          // Get names of deleted reports from current data
          const deletedReports = data
            .filter((report) => selectedIds.includes(report.id))
            .map((report) => ({ name: report.name }));

          setDeleteResults({
            success: deletedReports,
            failed: [],
          });
        } else {
          setDeleteError(result.message || t('reports.deleteConfirm.errorTitle'));
          setDeleteStatus('error');
          return;
        }
      }

      setDeleteStatus('results');
      refreshData();
      handleDeselectAll();
    } catch (error) {
      setDeleteStatus('error');
      setDeleteError(error.message || t('reports.deleteConfirm.errorTitle'));
    }
  }, [selectedReport, selectedRowIds, data, refreshData, handleDeselectAll, t]);

  const handleBulkDelete = useCallback(() => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    setSelectedReport(null);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, [selectedRowIds]);

  const handleBulkPublish = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      // Update each selected report to published status
      await Promise.all(
        selectedIds.map((id) =>
          updateDashboard(id, { status: STATUS_PUBLISHED }),
        ),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to publish reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleBulkUnpublish = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      // Update each selected report to draft status
      await Promise.all(
        selectedIds.map((id) => updateDashboard(id, { status: STATUS_DRAFT })),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to unpublish reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleSearchChange = useCallback(
    (newSearchTerm) => {
      setSearchTerm(newSearchTerm);
    },
    [setSearchTerm],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
    },
    [setFilters],
  );

  const handleSearchRemove = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setPageIndex((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(totalCount / pageSize);
    setPageIndex((prev) => Math.min(maxPage, prev + 1));
  }, [totalCount, pageSize]);

  const canPreviousPage = pageIndex > 1;
  const canNextPage = pageIndex < Math.ceil(totalCount / pageSize);

  const columns = useMemo(
    () =>
      createColumns(t, {
        onView: handleView,
        onEdit: handleEdit,
        onPublish: handlePublish,
        onUnpublish: handleUnpublish,
        onDelete: handleDeleteClick,
        onUpdateStatus: handleUpdateStatus,
        isAdmin,
      }),
    [
      t,
      handleView,
      handleEdit,
      handlePublish,
      handleUnpublish,
      handleDeleteClick,
      handleUpdateStatus,
      isAdmin,
    ],
  );

  const filterTypes = useMemo(() => getFilterTypes(t), [t]);
  const filterValues = useMemo(() => getFilterValues(t), [t]);

  const selectedReportsCount = useMemo(() => {
    if (selectAllMode) {
      const deselectedCount =
        Object.values(deselectedRowIds).filter(Boolean).length;
      return totalCount - deselectedCount;
    }
    return Object.values(selectedRowIds).filter(Boolean).length;
  }, [selectAllMode, totalCount, selectedRowIds, deselectedRowIds]);

  // Default reports are read-only: block mutating bulk actions when the
  // selection includes one (the server enforces this too).
  // In "select all" mode the selection spans every page — always including the
  // seeded default reports — and `data` only holds the current page, so we
  // can't reliably inspect it. Treat select-all as always containing a default.
  const selectionHasDefault = useMemo(() => {
    if (selectAllMode) {
      return true;
    }
    return data.some(
      (report) => report.isDefault && selectedRowIds[String(report.id)],
    );
  }, [data, selectAllMode, selectedRowIds]);

  const createButton = useMemo(
    () =>
      createAdminOnlyCreateButton(
        t,
        'reports.createButton',
        onCreateReport,
        isAdmin,
      ),
    [t, onCreateReport, isAdmin],
  );

  return (
    <div className={styles['reports-management-container']}>
      {!hideHeader && (
        <>
          <Breadcrumbs />
          <Header>{t('reports.title')}</Header>
        </>
      )}
      <div className={styles['table-wrapper']}>
        {!hideToolbar && (
          <Toolbar
            onSearchChange={handleSearchChange}
            onFiltersChange={handleFiltersChange}
            searchTerm={searchTerm}
            onSearchRemove={handleSearchRemove}
            filters={filters}
            filterTypes={filterTypes}
            filterValues={filterValues}
            createButton={createButton}
          />
        )}
        <TableWithPagination
          data={data}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          selectAllMode={selectAllMode}
          onSelectAllModeChange={setSelectAllMode}
          deselectedRowIds={deselectedRowIds}
          onDeselectedRowIdsChange={setDeselectedRowIds}
          totalCount={totalCount}
          columnSizing={columnSizing}
          onColumnSizingChange={setColumnSizing}
          columnSizingInfo={columnSizingInfo}
          onColumnSizingInfoChange={setColumnSizingInfo}
          getRowId={getRowId}
          onRowClick={handleView}
          pageIndex={pageIndex}
          pageSize={pageSize}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          onPageSizeChange={handlePageSizeChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      </div>
      {selectedReportsCount > 0 && (
        <SelectedReportsBar
          numberOfSelectedReports={selectedReportsCount}
          onDeselectAll={handleDeselectAll}
          onPublish={handleBulkPublish}
          onUnpublish={handleBulkUnpublish}
          onDelete={handleBulkDelete}
          defaultSelected={selectionHasDefault}
          disabledTooltip={t('reports.actions.defaultReadOnly')}
        />
      )}
      <DeleteModal
        isOpen={deleteModalOpen}
        deleteStatus={deleteStatus}
        deleteResults={deleteResults}
        deleteError={deleteError}
        openAccordion={openAccordion}
        setOpenAccordion={setOpenAccordion}
        selectedCount={selectedReport ? 1 : selectedReportsCount}
        onSubmit={handleDeleteSubmit}
        onClose={handleCloseDeleteModal}
        translationPrefix="reports.deleteConfirm"
        getItemName={(item) => item.name}
      />
    </div>
  );
}

ReportsManagement.propTypes = {
  onViewReport: PropTypes.func.isRequired,
  onEditReport: PropTypes.func.isRequired,
  onCreateReport: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  isAdmin: PropTypes.bool,
  userLoading: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    is_admin: PropTypes.bool,
  }),
  externalSearchTerm: PropTypes.string,
  externalFilters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          label: PropTypes.string,
        }),
      ]),
    }),
  ),
  onSearchChange: PropTypes.func,
  onFiltersChange: PropTypes.func,
};

ReportsManagement.defaultProps = {
  hideHeader: false,
  hideToolbar: false,
  isAdmin: false,
  userLoading: false,
  user: null,
  externalSearchTerm: undefined,
  externalFilters: undefined,
  onSearchChange: undefined,
  onFiltersChange: undefined,
};
